import { useRazorpay } from '@hooks/useRazorpay';
import { useGstRate } from '@hooks/useGstRate';
import { gstOn, withGst } from '@lib/gst';
import { Button, GradientText } from '@ui/index';
import { MessageCircle, Percent, ShieldCheck, Wallet } from 'lucide-react';
import { useEffect, useRef } from 'react';

/** First month rental for the WhatsApp Business API number. */
export const CONNECT_RENTAL_INR = 999;
/** Message-sending credit that lands in the wallet after the rental debit. */
export const CONNECT_CREDIT_INR = 500;
/** Total charged on the connect pay screen (₹999 rental + ₹500 credit). */
export const CONNECT_PAY_TOTAL_INR = CONNECT_RENTAL_INR + CONNECT_CREDIT_INR;

const inr = (n: number): string =>
  `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

export interface ConnectPlanPayProps {
  /** Fired once, after the Razorpay payment is confirmed and the wallet credited. */
  onPaid: () => void;
  /**
   * Amount to charge. Defaults to ₹1499 (₹999 rental + ₹500 credit). Callers
   * that must clear a larger wallet deficit can pass a higher amount; the
   * breakdown copy still explains the ₹999 + ₹500 split.
   */
  amount?: number;
}

/**
 * The connect-time plan + payment screen. Charges the customer up front so the
 * wallet is funded *before* any AiSensy provisioning runs: ₹999 pays the first
 * month's WhatsApp API rental (debited on provisioning) and ₹500 stays in the
 * wallet as message-sending credit. Reused by the onboarding Connect page and
 * the Add Number modal's insufficient-balance path.
 */
export function ConnectPlanPay({
  onPaid,
  amount = CONNECT_PAY_TOTAL_INR,
}: ConnectPlanPayProps): JSX.Element {
  const { initiate, isLoading, isSuccess } = useRazorpay();
  const firedRef = useRef(false);
  // `amount` is the wallet credit; GST is added on top at checkout by the
  // server-created Razorpay order. Show the real payable so nobody is
  // surprised at the payment sheet.
  const gstRatePct = useGstRate();
  const gstAmount = gstOn(amount, gstRatePct);
  const payable = withGst(amount, gstRatePct);

  useEffect(() => {
    if (isSuccess && !firedRef.current) {
      firedRef.current = true;
      onPaid();
    }
  }, [isSuccess, onPaid]);

  return (
    <div className="relative">
      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-primary text-white shadow-btn-green">
        <ShieldCheck size={22} strokeWidth={1.5} />
      </div>
      <h2 className="text-card-title text-apple-label">Activate WhatsApp sending</h2>
      <p className="mt-2 text-[14px] text-apple-gray-600">
        A one-time payment sets up your number and funds your first month. You'll verify
        with Meta right after.
      </p>

      <div className="mt-6 space-y-3 rounded-2xl border border-apple-gray-200 bg-white/60 p-4">
        <LineItem
          icon={<MessageCircle size={18} strokeWidth={1.5} />}
          title="First month rental"
          subtitle="WhatsApp Business API number"
          amount={inr(CONNECT_RENTAL_INR)}
        />
        <LineItem
          icon={<Wallet size={18} strokeWidth={1.5} />}
          title="Message credit"
          subtitle="Added to your wallet for sending messages"
          amount={inr(CONNECT_CREDIT_INR)}
        />
        <LineItem
          icon={<Percent size={18} strokeWidth={1.5} />}
          title={`GST (${gstRatePct}%)`}
          subtitle="Tax invoice emailed after payment"
          amount={inr(gstAmount)}
        />
        <div className="border-t border-apple-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold text-apple-label">Total today</span>
            <span className="text-[20px] font-bold tracking-apple-display">
              <GradientText>{inr(payable)}</GradientText>
            </span>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[12px] leading-relaxed text-apple-gray-500">
        The <span className="font-medium text-apple-gray-700">₹500 credit is required</span> —
        without wallet balance you can't send any messages. The ₹999 rental covers your first
        month; the ₹500 stays in your wallet to send with.
      </p>

      <div className="mt-6">
        <Button
          className="w-full"
          size="lg"
          onClick={() => void initiate(amount)}
          loading={isLoading}
        >
          {isLoading ? 'Opening payment…' : `Pay ${inr(payable)}`}
        </Button>
      </div>
    </div>
  );
}

function LineItem({
  icon,
  title,
  subtitle,
  amount,
}: {
  icon: JSX.Element;
  title: string;
  subtitle: string;
  amount: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-apple-gray-100 text-apple-gray-600">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-apple-label">{title}</div>
        <div className="text-[12px] text-apple-gray-500">{subtitle}</div>
      </div>
      <div className="text-[15px] font-semibold text-apple-label">{amount}</div>
    </div>
  );
}
